import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MessageCircle, ShieldCheck, Clock, BadgePercent, ChevronDown } from "lucide-react";
import { waLink, DEFAULT_WA_MESSAGE } from "@/config";

const HERO_IMG =
  "https://images.unsplash.com/photo-1436491865332-7a61a109cc05?q=80&w=1400&auto=format&fit=crop";

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const scrollTo = (e, href) => {
  e.preventDefault();
  document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      id="inicio"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen bg-navy grain overflow-hidden flex items-center pt-32 pb-20"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[46rem] h-[46rem] rounded-full bg-gold/10 blur-[140px]" />
        <div className="absolute bottom-0 -left-52 w-[38rem] h-[38rem] rounded-full bg-[#123a6b]/40 blur-[120px]" />
      </div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto w-full grid lg:grid-cols-12 gap-14 items-center"
      >
        <div className="lg:col-span-7">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            data-testid="hero-overline"
            className="font-mono text-xs uppercase tracking-[0.35em] text-gold mb-8"
          >
            Agência premium de viagens &amp; mobilidade global
          </motion.p>

          <h1
            data-testid="hero-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08] text-ivory"
          >
            <MaskedLine delay={0.25}>Sua experiência de</MaskedLine>
            <MaskedLine delay={0.4}>viagem, elevada ao</MaskedLine>
            <MaskedLine delay={0.55}>
              nível{" "}
              <em className="italic text-gold">premium.</em>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            data-testid="hero-subtitle"
            className="mt-8 text-base sm:text-lg text-ivory/65 leading-relaxed max-w-xl"
          >
            Passagens aéreas internacionais, envios expressos DHL, transferências
            Western Union, moda e vestuário na nossa loja e assessoria completa
            para naturalização brasileira — tudo em um só lugar, com atendimento
            humano em tempo real.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <a
              href={waLink(DEFAULT_WA_MESSAGE)}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-quote"
              className="inline-flex items-center gap-2 bg-gold text-navy font-semibold px-8 py-4 rounded-full hover:bg-gold-light hover:-translate-y-1 transition-all duration-300 shadow-[0_12px_36px_rgba(212,175,55,0.4)]"
            >
              <MessageCircle className="w-5 h-5" />
              Solicitar Cotação
            </a>
            <a
              href="#servicos"
              onClick={(e) => scrollTo(e, "#servicos")}
              data-testid="hero-cta-services"
              className="inline-flex items-center gap-2 border border-ivory/25 text-ivory font-medium px-8 py-4 rounded-full hover:border-gold hover:text-gold transition-all duration-300"
            >
              Explorar Serviços
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            data-testid="hero-stats"
            className="mt-14 flex flex-wrap gap-x-12 gap-y-6"
          >
            {[
              ["+1.200", "clientes atendidos"],
              ["24/7", "atendimento humano"],
              ["100%", "seguro e garantido"],
            ].map(([num, label]) => (
              <div key={label}>
                <p className="font-serif text-3xl font-bold text-gold">{num}</p>
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-ivory/50 mt-1">
                  {label}
                </p>
              </div>
            ))}
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative hidden lg:block">
          <motion.div
            initial={{ opacity: 0, y: 60, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div
              style={{ y: imgY }}
              className="relative overflow-hidden rounded-t-[11rem] rounded-b-3xl border border-gold/25 shadow-[0_40px_90px_rgba(0,0,0,0.5)]"
            >
              <img
                src={HERO_IMG}
                alt="Asa de avião sobre nuvens ao pôr do sol"
                data-testid="hero-image"
                className="w-full aspect-[4/5] object-cover scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/70 via-transparent to-navy/10" />
            </motion.div>

            <div className="absolute -left-10 top-16 animate-float-soft" data-testid="hero-badge-guaranteed">
              <div className="flex items-center gap-2.5 bg-navy-card/90 backdrop-blur-md border border-gold/30 rounded-2xl px-4 py-3 shadow-xl">
                <ShieldCheck className="w-5 h-5 text-gold" />
                <span className="text-sm font-semibold text-ivory">100% Garantido</span>
              </div>
            </div>
            <div
              className="absolute -right-6 top-1/2 animate-float-soft"
              style={{ animationDelay: "1.2s" }}
              data-testid="hero-badge-support"
            >
              <div className="flex items-center gap-2.5 bg-navy-card/90 backdrop-blur-md border border-gold/30 rounded-2xl px-4 py-3 shadow-xl">
                <Clock className="w-5 h-5 text-gold" />
                <span className="text-sm font-semibold text-ivory">Atendimento 24/7</span>
              </div>
            </div>
            <div
              className="absolute -left-6 bottom-14 animate-float-soft"
              style={{ animationDelay: "2s" }}
              data-testid="hero-badge-rates"
            >
              <div className="flex items-center gap-2.5 bg-navy-card/90 backdrop-blur-md border border-gold/30 rounded-2xl px-4 py-3 shadow-xl">
                <BadgePercent className="w-5 h-5 text-gold" />
                <span className="text-sm font-semibold text-ivory">Tarifas Exclusivas</span>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.a
        href="#servicos"
        onClick={(e) => scrollTo(e, "#servicos")}
        data-testid="hero-scroll-indicator"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 text-ivory/40 hover:text-gold transition-colors z-10"
        aria-label="Rolar para serviços"
      >
        <ChevronDown className="w-7 h-7 animate-bounce" />
      </motion.a>
    </section>
  );
};
